import React, { useCallback, useContext } from 'react';
import { AppContext } from 'src/components/app-context';
import { Panel, PanelHeader } from '@prisjakt/ui/panel';
import { Clickable } from 'src/components/clickable';
import type {
  SecondaryNavigationSlice as SecondaryNavigationSliceProps,
  ProductCollection,
} from '../../../../types';
import { trackProductCollectionSecondaryNavigationClick } from '../../tracking';
import SitemapLink from './sitemap-link';

import * as S from './styles';
import { H3Text, TitleSmallText } from '@prisjakt/ui/text/variants';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';

type Props = SecondaryNavigationSliceProps & {
  productCollection?: ProductCollection;
};

const SecondaryNavigationSlice = (props: Props) => {
  const { productCollection, navigationItems } = props;
  const { t } = useContext(AppContext);

  const onClick = useCallback(
    (event: React.MouseEvent<Element, MouseEvent>, index: number) => {
      window.scrollTo(0, 0);
      const href = event.currentTarget.getAttribute('href');
      if (href) {
        trackProductCollectionSecondaryNavigationClick(href);
        trackInternalNavigationClick({
          scope: InternalNavigationScope.SECONDARY_NAVIGATION,
          nextPage: href,
          index,
        });
      }
    },
    []
  );

  return navigationItems.length ? (
    <S.Wrapper>
      <Panel>
        <S.PanelHeaderRow>
          <PanelHeader>
            <H3Text>
              {t('category', 'product-collection.related-categories')}
            </H3Text>
          </PanelHeader>
          <S.ShowAllCategoriesRow
            containerProps={{ 'data-test': 'ShowAllCategoriesLink' }}
          >
            <SitemapLink productCollection={productCollection} />
          </S.ShowAllCategoriesRow>
        </S.PanelHeaderRow>
        <S.HorizontalScroll>
          <S.NavigationLinks data-test="SecondaryNavigationLinkList">
            {navigationItems.map(({ title, url }, index) => (
              <S.NavigationLink key={url} data-test="SecondaryNavigationLink">
                <Clickable to={url} onClick={e => onClick(e, index)}>
                  <TitleSmallText>{title}</TitleSmallText>
                </Clickable>
              </S.NavigationLink>
            ))}
          </S.NavigationLinks>
        </S.HorizontalScroll>
      </Panel>
    </S.Wrapper>
  ) : null;
};

export default SecondaryNavigationSlice;
